// src/modules/Courier/pages/CourierModal/pages/RouteOrderPage.jsx
import React, { useState, useEffect, useMemo } from 'react';
import api from '../../../../services/api';
import styles from '../styles/RouteOrderPage.module.css';
import { useRouteStore } from '../../Route/useRouteStore';

export default function RouteOrderPage({ onClose, routeId }) {
  // Получаем чеки (гарантировано массив благодаря || [])
  const receipts = useRouteStore(s => s.routeData.receipts) || [];

  // Инициализируем мапу порядков из поля sequence_number
  const [numbers, setNumbers] = useState(() =>
    receipts.reduce((acc, r) => {
      acc[r.id] = typeof r.sequence_number === 'number' ? r.sequence_number : 0;
      return acc;
    }, {})
  );

  // Если receipts придут позже (например, после загрузки), синхронизируем состояния
  useEffect(() => {
    setNumbers(
      receipts.reduce((acc, r) => {
        acc[r.id] = typeof r.sequence_number === 'number' ? r.sequence_number : 0;
        return acc;
      }, {})
    );
  }, [receipts]);

  const nextNum = useMemo(
    () => Math.max(0, ...Object.values(numbers)) + 1,
    [numbers]
  );
  const [modalId, setModalId] = useState(null);
  const [autoMode, setAutoMode] = useState(false);

  // Нажатие — проставить либо снять порядковый номер
  const assignSequential = id => {
    setNumbers(prev => {
      const cur = prev[id];
      const updated = { ...prev };
      if (cur) {
        // Убираем номер и сдвигаем все, кто был позже
        Object.entries(updated).forEach(([key, val]) => {
          if (val > cur) updated[key] = val - 1;
        });
        updated[id] = 0;
      } else {
        updated[id] = nextNum;
      }
      return updated;
    });
  };
  const maxOption = Math.min(nextNum, receipts.length);
  // Контекстное меню — присвоить произвольный номер
  const assignArbitrary = num => {
    setNumbers(prev => {
      const updated = { ...prev };
      const cur = updated[modalId] || 0;
      if (cur) {
        Object.entries(updated).forEach(([key, val]) => {
          if (val > cur) updated[key] = val - 1;
        });
      }
      Object.entries(updated).forEach(([key, val]) => {
        if (val >= num) updated[key] = val + 1;
      });
      updated[modalId] = num;
      return updated;
    });
    setModalId(null);
  };

  const handleAutoArrange = () => {
    setAutoMode(v => !v);
  };

  // Отправка единым запросом всей новой последовательности
  const handleSave = async () => {
    const orderPayload = Object.entries(numbers)
      .filter(([, num]) => num > 0)
      .sort(([, a], [, b]) => a - b) // здесь явный числовой компаратор
      .map(([id, num]) => ({ receiptId: Number(id), sequenceNumber: num }));

    try {
      await api.patch(`/routes/${routeId}/order`, { order: orderPayload });

      // Локально обновляем стор
      const updateReceipt = useRouteStore.getState().updateReceipt;
      orderPayload.forEach(({ receiptId, sequenceNumber }) => {
        updateReceipt({ id: receiptId, sequence_number: sequenceNumber });
      });

      onClose();
    } catch (err) {
      console.error('Ошибка сохранения порядка маршрута:', err);
    }
  };

  // Формируем отображаемый список:
  // — в авто-режиме: сначала все с номерами (по возрастанию), потом без номеров
  // — иначе: оригинальный порядок из стора без sort()
  const displayList = useMemo(() => {
    if (autoMode) return receipts;
    const withNum = receipts
      .filter(r => numbers[r.id] > 0)
      .slice() // создаём копию, чтобы не мутировать
      .sort((a, b) => numbers[a.id] - numbers[b.id]); // явный компаратор
    const withoutNum = receipts.filter(r => numbers[r.id] === 0);
    return [...withNum, ...withoutNum];
  }, [receipts, numbers, autoMode]);

  return (
    <div className={styles.root}>
      <ul className={styles.list}>
        {displayList.map(r => {
          const num = numbers[r.id] || 0;
          const bgColor =
            r.status === 'Закрыта'
              ? '#d4edda'
              : r.status === 'Не доставлено'
              ? '#f8d7da'
              : 'transparent';
          return (
            <li
              key={r.id}
              className={styles.item}
              style={{ backgroundColor: bgColor }}
              onClick={() => assignSequential(r.id)}
              onContextMenu={e => {
                e.preventDefault();
                setModalId(r.id);
              }}
            >
              <span className={styles.address}>{r.delivery_address}</span>
              {num > 0 && <span className={styles.badge}>{num}</span>}
            </li>
          );
        })}
      </ul>

      {modalId !== null && (
        <div className={styles.modalOverlay} onClick={() => setModalId(null)}>
          <div
            className={styles.modalContent}
            onClick={e => e.stopPropagation()}
          >
            <p>Выберите номер (1–{nextNum}):</p>
            <div className={styles.modalButtons}>
              {Array.from({ length: maxOption }, (_, i) => i + 1).map(n => (
                <button
                  key={n}
                  className={styles.modalButton}
                  onClick={() => assignArbitrary(n)}
                >
                  {n}
                </button>
              ))}
            </div>
            <button
              className={styles.modalCancel}
              onClick={() => setModalId(null)}
            >
              Отмена
            </button>
          </div>
        </div>
      )}

      <div className={styles.footer}>
        <button
          className={styles.autoArrange}
          onClick={handleAutoArrange}
        >
          {autoMode ? 'Выстроить по порядку' : 'Не выстраивать' }
        </button>
        <button
          className={styles.save}
          onClick={handleSave}
          disabled={Object.values(numbers).filter(n => n > 0).length === 0}
        >
          Сохранить
        </button>
      </div>
    </div>
  );
}
